import useAddAccountPreferredProduct from '@/api/mutations/materials/useAddAccountPreferredProduct';
import useSearchProductSpecs from '@/api/queries/materials/useSearchProductSpecs';
import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import useGetMyTaxRates from '@/api/queries/me/useGetMyTaxRates';
import { ProductSpecCategory, ProductSpecsQuery } from '@/gql/graphql';
import CompanySettingsTabBase from '@/ui/components/companySettings/CompanySettingsTabBase';
import SupplierSelectionControl from '@/ui/components/supplierSelectionControl/SupplierSelectionControl';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Card,
  Combobox,
  CurrencyInput,
  DividerWithLabel,
  Field,
  Form,
  RawInput,
  Select,
  SelectOption,
  SelectSizeVariant,
} from '@payaca/ambrosia';
import { useDebounce } from '@payaca/hooks';
import { FC, useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import { Redirect, useHistory, useParams } from 'react-router';
import { z } from 'zod/v4';
import AuthenticatedPageWrapper from '../pageWrappers/authenticatedPageWrapper/AuthenticatedPageWrapper';

export type ProductSpecForList = {
  domesticHotWaterLitres?: number | null;
  nominalOutputKw?: number | null;
  scopAtDefaultFlow?: number | null;
  dimensions?: {
    heightMm?: number | null;
    widthMm?: number | null;
    depthMm?: number | null;
  } | null;
};

export type PreferredProductExtraColumn = {
  header: string;
  /**
   * Receives the productSpec (may be null when the material has no linked
   * spec) and returns a string with units inline (e.g. "12kW", "170L").
   */
  render: (productSpec: ProductSpecForList | null | undefined) => string;
};

export type ProductSpecDetail = {
  label: string;
  render: (productSpec: ProductSpecForList | null | undefined) => string;
};

export type PreferredProductCategoryConfig = {
  key: 'HEAT_PUMP' | 'CYLINDER' | 'EMITTER';
  label: string;
  route: string;
  singular: string;
  /**
   * Category-specific columns rendered between Model and Supplier cost in
   * the preferred-products list. Order matches the array order.
   */
  extraColumns?: readonly PreferredProductExtraColumn[];
  /**
   * Category-specific rows rendered in the Specification card on the
   * add-preferred-product page, after Manufacturer and Model.
   */
  specDetails?: readonly ProductSpecDetail[];
};

const formatMm = (n: number | null | undefined): string =>
  n != null ? `${n}mm` : '-';

const HEAT_PUMP_OUTPUT: ProductSpecDetail = {
  label: 'Heat output',
  render: (spec) =>
    spec?.nominalOutputKw != null ? `${spec.nominalOutputKw}kW` : '-',
};

const CYLINDER_CAPACITY: ProductSpecDetail = {
  label: 'Capacity',
  render: (spec) =>
    spec?.domesticHotWaterLitres != null
      ? `${spec.domesticHotWaterLitres}L`
      : '-',
};

const HEIGHT_DETAIL: ProductSpecDetail = {
  label: 'Height',
  render: (spec) => formatMm(spec?.dimensions?.heightMm),
};

const WIDTH_DETAIL: ProductSpecDetail = {
  label: 'Width',
  render: (spec) => formatMm(spec?.dimensions?.widthMm),
};

const DEPTH_DETAIL: ProductSpecDetail = {
  label: 'Depth',
  render: (spec) => formatMm(spec?.dimensions?.depthMm),
};

const SCOP_DETAIL: ProductSpecDetail = {
  label: 'SCOP',
  render: (spec) =>
    spec?.scopAtDefaultFlow != null ? spec.scopAtDefaultFlow.toFixed(2) : '-',
};

export const CATEGORIES: readonly PreferredProductCategoryConfig[] = [
  {
    key: 'HEAT_PUMP',
    label: 'Heat Pumps',
    route: 'add-heat-pump',
    singular: 'heat pump',
    extraColumns: [{ header: 'Output', render: HEAT_PUMP_OUTPUT.render }],
    specDetails: [
      HEAT_PUMP_OUTPUT,
      SCOP_DETAIL,
      HEIGHT_DETAIL,
      WIDTH_DETAIL,
      DEPTH_DETAIL,
    ],
  },
  {
    key: 'CYLINDER',
    label: 'Water Cylinders',
    route: 'add-cylinder',
    singular: 'cylinder',
    extraColumns: [{ header: 'Capacity', render: CYLINDER_CAPACITY.render }],
    specDetails: [CYLINDER_CAPACITY, HEIGHT_DETAIL, WIDTH_DETAIL, DEPTH_DETAIL],
  },
  // Emitters are currently not shown, because we don't use them in estimate product matching.
] as const;

type ProductSpecSearchItem = ProductSpecsQuery['productSpecs']['items'][number];

type ProductSpecMeta = {
  manufacturer: string;
  modelNumber: string;
  isPreferred: boolean;
  materialId: string | null;
  hasSupplier: boolean;
  productSpec: ProductSpecForList;
};

const NEW_SUPPLIER_MATERIAL_SCHEMA = z.object({
  supplierId: z.string().min(1, 'Supplier is required'),
  unitPriceExcTax: z.number({ message: 'Cost price is required' }).positive(),
  taxRateId: z.string().min(1, 'Tax rate is required'),
  reference: z.string().optional(),
  url: z.string().optional(),
});

type NewSupplierMaterialFormState = z.infer<
  typeof NEW_SUPPLIER_MATERIAL_SCHEMA
>;

const ProductSpecOption: FC<{
  option: SelectOption<string, ProductSpecMeta>;
  active: boolean;
  selected: boolean;
  close: () => void;
}> = (props) => {
  const { option } = props;

  return (
    <div className="flex w-full items-center justify-between">
      <div>
        <p className="font-medium">{option.label}</p>
      </div>
      <div className="flex items-center gap-2">
        {option.metadata?.isPreferred && (
          <span className="text-sm text-gray-400">Added</span>
        )}
        {option.metadata?.materialId && (
          <span className="text-green-600" title="Material exists">
            &#10003;
          </span>
        )}
      </div>
    </div>
  );
};

const NewSupplierMaterialForm: FC = () => {
  const { defaultTaxRate, taxRates } = useGetMyTaxRates();
  const { data: regionPreferences } = useGetMyRegionPreferences();
  const { setValue, getValues } =
    useFormContext<NewSupplierMaterialFormState>();

  useEffect(() => {
    if (defaultTaxRate?.id && !getValues('taxRateId')) {
      setValue('taxRateId', defaultTaxRate.id);
    }
  }, [defaultTaxRate?.id, getValues, setValue]);

  const taxRateOptions = useMemo(
    () =>
      taxRates
        ?.filter((tr) => !tr.isReverseCharge)
        .map((tr) => ({ label: tr.label, value: tr.id })) ?? [],
    [taxRates]
  );

  return (
    <Card sizeVariant="sm">
      <Card.Body className="flex flex-col gap-3">
        <p className="font-semibold">Supplier &amp; cost price</p>
        <p className="text-sm text-gray-500">
          This product has no supplier yet. Add one to start using it in
          estimates.
        </p>

        <Controller<NewSupplierMaterialFormState, 'supplierId'>
          name="supplierId"
          render={({ field: { name, onChange, value } }) => (
            <Field name={name}>
              <Field.Label>Supplier</Field.Label>
              <SupplierSelectionControl
                selectedSupplierId={value ? +value : undefined}
                onChange={(id) => onChange(id != null ? `${id}` : '')}
                enableSupplierCreation
                excludeSystemManaged
              />
            </Field>
          )}
        />

        <div className="flex gap-x-4">
          <Controller<NewSupplierMaterialFormState, 'unitPriceExcTax'>
            name="unitPriceExcTax"
            render={({ field: { name, onChange, value } }) => (
              <Field name={name}>
                <Field.Label>Cost price (ex tax)</Field.Label>
                <CurrencyInput
                  sizeVariant="sm"
                  inputClassName="text-right"
                  value={value}
                  onChange={onChange}
                  currency={regionPreferences?.currency}
                />
              </Field>
            )}
          />
          <Controller<NewSupplierMaterialFormState, 'taxRateId'>
            name="taxRateId"
            render={({ field: { name, onChange, value } }) => (
              <Field name={name}>
                <Field.Label>Tax rate</Field.Label>
                <Select
                  sizeVariant={SelectSizeVariant.SM}
                  value={value}
                  onChange={onChange}
                  options={taxRateOptions}
                />
              </Field>
            )}
          />
        </div>

        <Controller<NewSupplierMaterialFormState, 'reference'>
          name="reference"
          render={({ field }) => (
            <Field name={field.name}>
              <Field.Label>
                Supplier reference <Field.Optional />
              </Field.Label>
              <RawInput {...field} />
            </Field>
          )}
        />
        <Controller<NewSupplierMaterialFormState, 'url'>
          name="url"
          render={({ field }) => (
            <Field name={field.name}>
              <Field.Label>
                Product URL <Field.Optional />
              </Field.Label>
              <RawInput {...field} />
            </Field>
          )}
        />
      </Card.Body>
    </Card>
  );
};

const AddPreferredProductContent: FC<{
  category: ProductSpecCategory;
  categoryLabel: string;
  specDetails?: readonly ProductSpecDetail[];
  onClose: () => void;
}> = (props) => {
  const { category, categoryLabel, specDetails, onClose } = props;

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedSpecId, setSelectedSpecId] = useState<string | undefined>();

  useDebounce(() => setDebouncedQuery(query), 300, [query]);

  const { data: results } = useSearchProductSpecs(
    category,
    debouncedQuery || undefined
  );

  const options: SelectOption<string, ProductSpecMeta>[] = useMemo(
    () =>
      results?.map((result: ProductSpecSearchItem) => ({
        label: result.productSpec.name,
        value: result.productSpec.id,
        disabled: result.isPreferred,
        metadata: {
          manufacturer: result.productSpec.manufacturer,
          modelNumber: result.productSpec.modelNumber,
          isPreferred: result.isPreferred,
          materialId: result.material?.id ?? null,
          hasSupplier: (result.material?.suppliedBy.length ?? 0) > 0,
          productSpec: result.productSpec,
        },
      })) ?? [],
    [results]
  );

  const selectedOption = options.find((o) => o.value === selectedSpecId);
  const needsSupplierInputs =
    selectedOption != null && !selectedOption.metadata?.hasSupplier;

  const formMethods = useForm<NewSupplierMaterialFormState>({
    resolver: zodResolver(NEW_SUPPLIER_MATERIAL_SCHEMA),
    defaultValues: {
      supplierId: '',
      taxRateId: '',
      reference: '',
      url: '',
    },
  });

  const { mutate, isPending } = useAddAccountPreferredProduct();

  const submitWith = (
    newSupplierMaterial: NewSupplierMaterialFormState | null
  ) => {
    if (!selectedSpecId) return;
    mutate(
      {
        productSpecId: selectedSpecId,
        newSupplierMaterial: newSupplierMaterial
          ? {
              supplierId: newSupplierMaterial.supplierId,
              unitPriceExcTax: newSupplierMaterial.unitPriceExcTax,
              taxRateId: newSupplierMaterial.taxRateId,
              reference: newSupplierMaterial.reference || null,
              url: newSupplierMaterial.url || null,
            }
          : null,
      },
      { onSuccess: onClose }
    );
  };

  const submitDisabled = !selectedSpecId;

  return (
    <div className="p-4 grid gap-6">
      <Card>
        <Card.Body>
          <div className="typography:prose mb-4">
            <h3>Add {categoryLabel}</h3>
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium">Search</label>
            <Combobox
              multiple={false}
              options={options}
              value={selectedSpecId}
              onChange={(value) => setSelectedSpecId(value)}
              query={query}
              setQuery={setQuery}
              filterFunction={null}
              placeholder="Search by name, brand or model"
              inputPlaceholder="Search by name, brand or model"
              CustomOption={ProductSpecOption}
            />
            <Alert colour="blue" className="mt-4" iconName="info-circle.3">
              {` Can't find the ${categoryLabel} you're looking for? Let
              us know so we can add it in for you.`}
            </Alert>
          </div>
        </Card.Body>
      </Card>
      {selectedOption && (
        <>
          <DividerWithLabel label="Specification" />

          <Card sizeVariant="sm">
            <Card.Body>
              <div className="typography:prose grid gap-3 md:grid-cols-2">
                <p className="font-semibold md:col-span-2">
                  {selectedOption.label}
                </p>
                <div>
                  <p className="text-sm text-gray-500">Manufacturer</p>
                  <p>{selectedOption.metadata?.manufacturer}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Model</p>
                  <p>{selectedOption.metadata?.modelNumber}</p>
                </div>
                {specDetails?.map((detail) => (
                  <div key={detail.label}>
                    <p className="text-sm text-gray-500">{detail.label}</p>
                    <p>{detail.render(selectedOption.metadata?.productSpec)}</p>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
          {needsSupplierInputs ? (
            <Form
              methods={formMethods}
              onSubmit={(values) => submitWith(values)}
              className="grid gap-6"
            >
              <NewSupplierMaterialForm />
              <div className="flex justify-end">
                <Button
                  type="submit"
                  isProcessing={isPending}
                  disabled={submitDisabled}
                >
                  Add {categoryLabel}
                </Button>
              </div>
            </Form>
          ) : (
            <div className="flex justify-end">
              <Button
                isProcessing={isPending}
                disabled={submitDisabled}
                onClick={() => submitWith(null)}
              >
                Add {categoryLabel}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const AddPreferredProductPage: FC = () => {
  const { action } = useParams<{ action: string }>();
  const history = useHistory();

  const category = CATEGORIES.find((c) => c.route === action);

  if (!category) {
    return <Redirect to="/404" />;
  }

  return (
    <AuthenticatedPageWrapper
      breadcrumbItems={[
        { label: 'Company settings', to: '/companySettings' },
        {
          label: 'Estimates',
          to: '/companySettings/estimates',
        },
        { label: `Add ${category.singular}` },
      ]}
    >
      <CompanySettingsTabBase>
        <AddPreferredProductContent
          category={category.key}
          categoryLabel={category.singular}
          specDetails={category.specDetails}
          onClose={() => history.push('/companySettings/estimates')}
        />
      </CompanySettingsTabBase>
    </AuthenticatedPageWrapper>
  );
};

export default AddPreferredProductPage;
